import React, { useState, useCallback, useRef } from "react";
import { Modal, Button, Space, InputNumber, Tooltip, Spin } from "antd";
import { Document, Page, pdfjs } from "react-pdf";
import {
  LeftOutlined,
  RightOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
  CloseOutlined,
  PrinterOutlined,
} from "@ant-design/icons";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { StyledPdfViewer } from "./styles";
import { useMediaHandler } from "../../../hooks/useMediaHandler";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const PdfViewer = ({ pdfUrl, onClose, onError }) => {
  const { handleError, isLoading, handleLoaded } = useMediaHandler({
    src: pdfUrl,
    onError,
  });
  const [numPages, setNumPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [scale, setScale] = useState(1);

  const onDocumentLoadSuccess = useCallback(({ numPages }) => {
    handleLoaded();
    setNumPages(numPages);
    setCurrentPage(1);
  }, []);

  const goToPrevPage = useCallback(() => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  }, []);

  const goToNextPage = useCallback(() => {
    setCurrentPage((prev) => Math.min(prev + 1, numPages || 1));
  }, [numPages]);

  const handlePageChange = useCallback(
    (value) => {
      if (value >= 1 && value <= numPages) {
        setCurrentPage(value);
      }
    },
    [numPages],
  );

  const handleZoomIn = useCallback(() => {
    setScale((prev) => Math.min(prev + 0.25, 2.5));
  }, []);

  const handleZoomOut = useCallback(() => {
    setScale((prev) => Math.max(prev - 0.25, 0.5));
  }, []);

  const handleResetZoom = useCallback(() => {
    setScale(1.2);
  }, []);

  const handlePrint = useCallback(() => {
    if (!pdfUrl) return;

    fetch(pdfUrl)
      .then((response) => response.blob())
      .then((blob) => {
        const blobUrl = URL.createObjectURL(blob);
        const iframe = document.createElement("iframe");
        iframe.style.display = "none";
        iframe.src = blobUrl;
        iframe.onload = () => {
          setTimeout(() => {
            iframe.contentWindow.print();
            URL.revokeObjectURL(blobUrl);
          }, 500);
        };
        document.body.appendChild(iframe);
      });
  }, [pdfUrl]);

  return (
    <Modal
      open
      onCancel={onClose}
      footer={null}
      width="90vw"
      title={null}
      centered
      styles={{
        body: {
          padding: 0,
          height: "90dvh",
        },
      }}
      closeIcon={null}
    >
      <StyledPdfViewer>
        <div className="pdf-viewer-wrapper">
          {/* Header Controls */}
          <div className="pdf-header">
            <div className="pdf-title">PDF Viewer</div>

            <div className="flex align-center">
              <Tooltip title="Print PDF">
                <Button
                  type="primary"
                  icon={<PrinterOutlined />}
                  onClick={handlePrint}
                  className="print-pdf"
                />
              </Tooltip>
              <Space size="large">
                {/* Navigation Controls */}
                <div className="control-group">
                  <Tooltip title="Previous Page">
                    <Button
                      type="text"
                      icon={<LeftOutlined />}
                      onClick={goToPrevPage}
                      disabled={currentPage <= 1}
                      className="nav-button"
                    />
                  </Tooltip>

                  <div className="page-indicator">
                    <InputNumber
                      min={1}
                      max={numPages || 1}
                      value={currentPage}
                      onChange={handlePageChange}
                      className="page-input"
                      controls={false}
                    />
                    <span className="page-divider">/</span>
                    <span className="total-pages">{numPages || 0}</span>
                  </div>

                  <Tooltip title="Next Page">
                    <Button
                      type="text"
                      icon={<RightOutlined />}
                      onClick={goToNextPage}
                      disabled={currentPage >= numPages}
                      className="nav-button"
                    />
                  </Tooltip>
                </div>

                {/* Zoom Controls */}
                <div className="control-group zoom-group">
                  <Tooltip title="Zoom Out">
                    <Button
                      type="text"
                      icon={<ZoomOutOutlined />}
                      onClick={handleZoomOut}
                      disabled={scale <= 0.5}
                      className="zoom-button"
                    />
                  </Tooltip>

                  <Button
                    type="text"
                    onClick={handleResetZoom}
                    className="zoom-display"
                  >
                    {Math.round(scale * 100)}%
                  </Button>

                  <Tooltip title="Zoom In">
                    <Button
                      type="text"
                      icon={<ZoomInOutlined />}
                      onClick={handleZoomIn}
                      disabled={scale >= 2.5}
                      className="zoom-button"
                    />
                  </Tooltip>
                  <Tooltip title="Cancel">
                    <Button
                      type="text"
                      icon={<CloseOutlined />}
                      onClick={onClose}
                    />
                  </Tooltip>
                </div>
              </Space>
            </div>
          </div>

          {/* PDF Document Container */}
          <div className="pdf-document-wrapper">
            <Document
              file={pdfUrl}
              onLoadSuccess={onDocumentLoadSuccess}
              onLoadError={handleError}
              loading={
                <div className="pdf-status">
                  <Spin />
                  <p>Loading PDF...</p>
                </div>
              }
              error={
                <>
                  {isLoading ? (
                    <div className="pdf-status">
                      <Spin />
                      <p>Loading PDF...</p>
                    </div>
                  ) : (
                    <div className="pdf-status error">
                      <p>Failed to load PDF file</p>
                      <small>Please check the file URL or try again</small>
                    </div>
                  )}
                </>
              }
            >
              <Page
                pageNumber={currentPage}
                scale={scale}
                renderTextLayer={true}
                renderAnnotationLayer={true}
                loading={
                  <div className="page-loading">
                    <Spin />
                  </div>
                }
              />
            </Document>
          </div>
        </div>
      </StyledPdfViewer>
    </Modal>
  );
};

export default PdfViewer;
